import { Text, TextProps } from '@chakra-ui/react'
import { Task_Status_Enum } from '@gql'
import { ThreadWeather } from '@rolebase/shared/model/threadWeather'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SummaryPiece } from '../meetingSummaryShareBoard'

interface Props extends TextProps {
  piece: SummaryPiece
}

/**
 * The facts of a piece of the compte rendu (#132): weather, role, person,
 * status… Set by the server, they go out as they are — the member can only
 * rewrite the text below them. One line, in the words of the app.
 */
export default function MeetingSummaryPieceFacts({ piece, ...textProps }: Props) {
  const { t } = useTranslation()
  const meta = piece.meta
  if (!meta) return null

  const facts: (string | undefined)[] = []
  switch (meta.kind) {
    case 'thread':
      facts.push(
        meta.weather && t(`ThreadWeather.${meta.weather as ThreadWeather}`),
        meta.roleName,
        meta.memberName,
        meta.closed === false ? t('common.threadStatus.Active') : undefined
      )
      break
    case 'nextStep':
      facts.push(
        meta.memberName,
        meta.done
          ? t('MeetingSummaryShareModal.facts.done')
          : meta.born
            ? t('MeetingSummaryShareModal.facts.new')
            : undefined
      )
      break
    case 'task':
      facts.push(
        meta.status && t(`common.taskStatus.${meta.status as Task_Status_Enum}`),
        meta.memberName,
        meta.isNew ? t('MeetingSummaryShareModal.facts.new') : undefined
      )
      break
    case 'roleReview':
      facts.push(...(meta.others ?? []))
      break
    case 'decision':
      if (meta.policy) {
        facts.push(t('MeetingSummaryShareModal.facts.policy', { title: meta.policy }))
      }
      for (const proposal of meta.proposals ?? []) {
        facts.push(
          t('MeetingSummaryShareModal.facts.proposal', {
            title: proposal.title,
            count: proposal.changes.length,
          })
        )
      }
      break
    case 'attendees':
      facts.push(
        t('MeetingSummaryShareModal.facts.attendance', {
          present: meta.present?.length ?? 0,
          invited: (meta.present?.length ?? 0) + (meta.absent?.length ?? 0),
        })
      )
      break
  }

  const line = facts.filter(Boolean).join(' · ')
  if (!line) return null
  return (
    <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }} {...textProps}>
      {line}
    </Text>
  )
}
